/**
 * clasificadores/evolucionMatriz.js — Clasificador puro del dashboard
 * Evolución de la Matriz (réplica del modelo, 12-jul-2026).
 *
 * La Matriz simple mira la FOTO (30 días); este clasificador mira la
 * PELÍCULA: la serie mensual de cuadrantes de cada publicación (mismos
 * umbrales 1.15/0.85 y mismo `detectarPatron` que el dashboard) y convierte
 * las trayectorias en tareas.
 *
 * 3 secciones (decisión Mariano 12-jul):
 *   caida_matriz     → ⚠️ En caída: venía bien y viene cayendo →
 *                      revisar_competitividad (alta). EXCLUYE las caídas con
 *                      stock 0 (sospecha de quiebre — la causa es stock, no
 *                      competitividad: Velocidad ya pide reponer; declarada).
 *   cronica_matriz   → 🪦 Crónica: eventual/incógnita TODOS los meses →
 *                      revisar_competitividad (baja, backlog). Orden: capital
 *                      atrapado (stock × precio) DESC. Si no hay arreglo, la
 *                      conclusión de la tarea es discontinuar/liquidar.
 *   despertar_matriz → 🚀 Despertando: venía baja y está subiendo sola →
 *                      activar_ad (solo si NO tiene ad inscripto — cualquier
 *                      estado, mismo criterio que la Matriz; si ya tiene,
 *                      declarada).
 * Declaradas (sin tarea): ⭐ Core (no se toca) · 🔥 Recuperando (ya vuelve
 * sola) · 🔄 Volátil (impredecible) · ⏸ Estable · sin ventas en el período ·
 * estrella con margen negativo (dominio de Contribución Neta) · caídas por
 * quiebre · despertando con ad.
 *
 * MES EN CURSO: se EXCLUYE de la serie (umbrales.incluirMesEnCurso para
 * incluirlo). El dashboard lo muestra (parcial); para TAREAS un mes recién
 * empezado genera falsas caídas — la detección usa N meses COMPLETOS.
 *
 * Métrica: UNIDADES (la default del dashboard). Ventana: umbrales.meses
 * (default 6 meses completos).
 *
 * Input: { publicaciones (legacy shape), ventasPeriodo (shape cascada, la
 *          ventana la da diasVentas del registry — cubre los N meses),
 *          adsItems (ads inscriptos, cualquier estado), umbrales }
 */

import { gateAdsPrender, construirInfoRentabilidadPorItem } from './gatePromoRentabilidad'

export const MESES_DEFAULT = 6
const UMBRAL_SUPERIOR = 1.15   // mismos cortes que MatrizDistribucion/Evolución
const UMBRAL_INFERIOR = 0.85

// Ranks espejo del dashboard (CUADRANTES de EvolucionMatrizDashboard).
export const RANK_CUADRANTE = { estrella: 4, moderada: 3, perro: 2, incognita: 1 }
const LABEL_CUADRANTE = { estrella: '⭐ estrella', moderada: '🐄 moderada', perro: '📉 eventual', incognita: '❓ incógnita' }

// ─── Patrones de trayectoria — ESPEJO de detectarPatron del dashboard ──────
export const PATRONES_EVOLUCION = {
  core:        { id: 'core',        emoji: '⭐', label: 'Core' },
  despertando: { id: 'despertando', emoji: '🚀', label: 'Despertando' },
  recuperando: { id: 'recuperando', emoji: '🔥', label: 'Recuperando' },
  caida:       { id: 'caida',       emoji: '⚠️', label: 'En caída' },
  volatil:     { id: 'volatil',     emoji: '🔄', label: 'Volátil' },
  cronica:     { id: 'cronica',     emoji: '🪦', label: 'Crónica' },
  estable:     { id: 'estable',     emoji: '⏸',  label: 'Estable' },
}

// Detecta el patrón de trayectoria a partir de la serie de cuadrantes.
// VERBATIM del dashboard (misma cascada de especificidad) — si cambia allá,
// cambiarlo acá (candidato natural a compartirse cuando toquemos el dashboard).
export function detectarPatronEvolucion(serie) {
  const ranks = serie.map(s => RANK_CUADRANTE[s.cuadrante])
  const n = ranks.length
  if (n < 2) return PATRONES_EVOLUCION.estable

  // Core: Estrella (rank 4) en todos los meses, mínimo 3 puntos.
  if (n >= 3 && ranks.every(r => r === 4)) return PATRONES_EVOLUCION.core

  // Crónica: siempre Inc/Perro (rank ≤ 2).
  if (ranks.every(r => r <= 2)) return PATRONES_EVOLUCION.cronica

  const first = ranks[0]
  const last  = ranks[n - 1]
  const min   = Math.min(...ranks)
  // Cantidad de cambios entre puntos consecutivos
  let cambios = 0
  for (let i = 1; i < n; i++) if (ranks[i] !== ranks[i - 1]) cambios++

  // Volátil: con n>=4, hubo al menos 3 cambios en la serie.
  if (n >= 4 && cambios >= 3) return PATRONES_EVOLUCION.volatil

  // Recuperando: empezó alto o medio, tuvo caída y está volviendo (V).
  if (n >= 3 && (min < first - 0.5) && (last > min + 0.5)) {
    return PATRONES_EVOLUCION.recuperando
  }

  // En caída: terminó más bajo que como empezó (con margen ≥ 1 rank).
  if (last < first - 0.5) return PATRONES_EVOLUCION.caida

  // Despertando: empezó bajo (rank ≤ 2) y terminó más alto.
  if (first <= 2 && last > first + 0.5) return PATRONES_EVOLUCION.despertando

  return PATRONES_EVOLUCION.estable
}

// ─── Helpers de calendario (espejo del dashboard) ───────────────────────────
function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function monthLabel(d) {
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${meses[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`
}
export function ultimosNMesesCompletos(n, { incluirMesEnCurso = false, ahora = new Date() } = {}) {
  const out = []
  const offset = incluirMesEnCurso ? 0 : 1   // sin mes en curso: termina en el mes pasado
  for (let i = n - 1 + offset; i >= offset; i--) {
    const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1)
    out.push({ key: monthKey(d), label: monthLabel(d) })
  }
  return out
}

// Mismo criterio que el dashboard: incógnita = sin ventas; ratio vs promedio.
// Exportada (26-sep-2026): la Matriz y la Evolución de Tienda Nube clasifican
// con ESTA función — un cuadrante, una regla, los dos canales.
export function clasificarCuadrante(valor, promedio) {
  if (!valor || valor <= 0) return 'incognita'
  if (!promedio || promedio <= 0) return 'moderada'
  const ratio = valor / promedio
  if (ratio > UMBRAL_SUPERIOR) return 'estrella'
  if (ratio < UMBRAL_INFERIOR) return 'perro'
  return 'moderada'
}

const esActiva = (p) => p?.status === 'active' || p?.estado === 'activa'
const itemIdDe = (p) => String(p?.itemId || p?.externalId || p?.id || '')

const fechaDe = (v) => {
  if (v?._dateCreated instanceof Date) return v._dateCreated
  const raw = v?.date_created || v?.fechaCreada || null
  if (!raw) return null
  const d = new Date(raw)
  return Number.isFinite(d.getTime()) ? d : null
}

const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString('es-AR')}`

export function clasificarEvolucionMatriz({
  publicaciones = [],
  ventasPeriodo = null,
  ventas30d = [],          // alias del loader (la ventana real la da diasVentas)
  adsItems = [],
  umbrales = {},
  config = {},           // doc empresa (para _benchmarksML)
  ecosistema = null,     // conocimientoEcosistema/costos-ml (loader)
  impuestosEmpresa = null,
  productos = [],
  catalogoLinks = [],
  categorias = [],
  marcas = [],
  proveedores = [],
  productoSourcing = [],
  rentabilidadEmpresa = null,
} = {}) {
  // 15-jul: corte por cascada ADR 032 para el gate de ads (despertar).
  const infoRentPorItemAds = construirInfoRentabilidadPorItem({
    productos, catalogoLinks, categorias, marcas, proveedores, productoSourcing,
    empresaMin: rentabilidadEmpresa?.rentabilidadMinima ?? null,
  })
  const ventas = ventasPeriodo || ventas30d || []
  const nMeses = Number(umbrales.meses) >= 2 ? Number(umbrales.meses) : MESES_DEFAULT
  const meses = ultimosNMesesCompletos(nMeses, { incluirMesEnCurso: umbrales.incluirMesEnCurso === true })
  const mesKeys = new Set(meses.map(m => m.key))

  // ── Ads inscriptos (cualquier estado — mismo criterio que la Matriz) ──
  const itemsConAd = new Set()
  for (const ad of adsItems || []) {
    const itemId = ad?.item_id || ad?.itemId || (ad?.id ? String(ad.id).split('_').pop() : null)
    if (itemId) itemsConAd.add(String(itemId))
  }
  const conAdDe = (p) => p?.facetas?.conAd === true || itemsConAd.has(itemIdDe(p))

  // ── Agregación: itemId → { [monthKey]: { unidades, facturacion } } ──
  const porItemPorMes = new Map()
  for (const v of ventas) {
    const fecha = fechaDe(v)
    if (!fecha) continue
    const mk = monthKey(fecha)
    if (!mesKeys.has(mk)) continue   // fuera de la ventana de meses completos
    const items = Array.isArray(v?.order_items) ? v.order_items : []
    for (const oi of items) {
      const itemId = oi?.item?.id
      if (!itemId) continue
      const qty = Number(oi.quantity || 0)
      if (qty <= 0) continue
      const revenue = qty * Number(oi.unit_price || 0)
      const k = String(itemId)
      const porMes = porItemPorMes.get(k) || new Map()
      const cur = porMes.get(mk) || { unidades: 0, facturacion: 0 }
      cur.unidades += qty
      cur.facturacion += revenue
      porMes.set(mk, cur)
      porItemPorMes.set(k, porMes)
    }
  }

  // ── Promedio de unidades por mes (excluye incógnitas — espejo dashboard) ──
  const activas = publicaciones.filter(esActiva)
  const avgPorMes = {}
  for (const m of meses) {
    const vals = []
    for (const p of activas) {
      const u = porItemPorMes.get(itemIdDe(p))?.get(m.key)?.unidades || 0
      if (u > 0) vals.push(u)
    }
    avgPorMes[m.key] = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
  }

  // ── Serie + patrón por publicación ──
  const caida_matriz = []
  const cronica_matriz = []
  const despertar_matriz = []
  const declarados = { core: 0, recuperando: 0, volatil: 0, estable: 0 }
  let sinVentasPeriodo = 0
  let pausadas = publicaciones.length - activas.length
  let caidaPorQuiebre = 0
  let despertandoConAd = 0
  let despertandoSinStock = 0
  let despertandoAPerdida = 0
  let despertandoSinEspacio = 0
  let estrellaMargenNegativo = 0

  for (const p of activas) {
    const itemId = itemIdDe(p)
    const porMes = porItemPorMes.get(itemId)

    // Sin ventas en NINGÚN mes del período → fuera (espejo del toggle default
    // del dashboard). Ese universo ya lo trabaja la Matriz simple (publicitar).
    if (!porMes || porMes.size === 0) { sinVentasPeriodo++; continue }

    const serie = meses.map(m => {
      const data = porMes.get(m.key) || { unidades: 0, facturacion: 0 }
      return { monthKey: m.key, label: m.label, cuadrante: clasificarCuadrante(data.unidades, avgPorMes[m.key]), data }
    })
    const patron = detectarPatronEvolucion(serie)
    const stockActual = Number(p.availableQuantity ?? p.available_quantity ?? 0)
    const precio = Number(p.price) || 0

    // Estrella actual con margen negativo — señal fuerte pero de OTRO dueño
    // (Contribución Neta ya trabaja las órdenes a pérdida). Declarada.
    const margenPct = p?._rentabilidadInferida?.margenPctProm
    if (serie[serie.length - 1].cuadrante === 'estrella' && Number.isFinite(margenPct) && margenPct < 0) {
      estrellaMargenNegativo++
    }

    const serieTexto = serie.map(s => LABEL_CUADRANTE[s.cuadrante].split(' ')[1]).join(' → ')
    const factMes = (i) => serie[i]?.data?.facturacion || 0
    const base = {
      id: itemId,
      itemId,
      _itemId: itemId,
      title: p.title ?? p.titulo ?? null,
      price: precio || null,
      permalink: p.permalink ?? null,
      _patron: patron.id,
      _serieTexto: serieTexto,
      _stock: stockActual,
    }

    if (patron.id === 'caida') {
      // Caída con stock 0 = sospecha de quiebre (espejo del dashboard): la
      // causa probable es stock, no competitividad → Velocidad pide reponer.
      if (stockActual <= 0) { caidaPorQuiebre++; continue }
      const factInicio = factMes(0)
      const factFin = factMes(serie.length - 1)
      caida_matriz.push({
        ...base,
        _factPerdida: Math.max(0, factInicio - factFin),
        _motivo: `⚠️ Venía ${LABEL_CUADRANTE[serie[0].cuadrante]} y cayó a ${LABEL_CUADRANTE[serie[serie.length - 1].cuadrante]} en ${meses.length} meses (${serieTexto}) — facturaba ${money(factInicio)}/mes y pasó a ${money(factFin)}. Investigá qué cambió: precio vs competencia, buy-box, publi, promo vencida.`,
        metricas: { patron: patron.id, serie: serieTexto, meses: meses.length, facturacionInicio: factInicio, facturacionFin: factFin, stock: stockActual },
      })
    } else if (patron.id === 'cronica') {
      const capitalAtrapado = stockActual * precio
      cronica_matriz.push({
        ...base,
        _capitalAtrapado: capitalAtrapado,
        _motivo: `🪦 ${meses.length} meses en eventual/incógnita sin asomar nunca (${serieTexto})${capitalAtrapado > 0 ? ` — capital atrapado: ${money(capitalAtrapado)} (${stockActual} uds)` : ''}. Revisá precio/listing vs competencia; si no hay arreglo, evaluá discontinuar o liquidar.`,
        metricas: { patron: patron.id, serie: serieTexto, meses: meses.length, stock: stockActual, capitalAtrapado },
      })
    } else if (patron.id === 'despertando') {
      if (conAdDe(p)) { despertandoConAd++; continue }
      // Flywheel (14-jul-2026): no se prende un ad sobre algo sin stock (no hay
      // qué vender) ni sobre pérdida conocida (Contribución Neta la trabaja).
      if (stockActual <= 0) { despertandoSinStock++; continue }
      // GATE DE ADS (15-jul): margen real − ACOS esperado (cuenta → eco).
      const rentAd = infoRentPorItemAds.get(String(itemId)) || { costoUnit: null, corte: null }
      const gateAd = gateAdsPrender({
        precio,
        margenReal: Number.isFinite(margenPct) ? margenPct : null,
        costoUnit: rentAd.costoUnit, corte: rentAd.corte,
        categoryId: p?.datosCanal?.categoryId ?? null,
        ecosistema, impuestosEmpresa,
        benchmarksEmpresa: config?._benchmarksML || null,
      })
      if (gateAd.estado === 'veto_perdida') { despertandoAPerdida++; continue }
      if (gateAd.estado === 'sin_espacio') { despertandoSinEspacio++; continue }
      despertar_matriz.push({
        ...base,
        _factUltimoMes: factMes(serie.length - 1),
        _motivo: `🚀 Venía ${LABEL_CUADRANTE[serie[0].cuadrante]} y subió a ${LABEL_CUADRANTE[serie[serie.length - 1].cuadrante]} (${serieTexto}) — está agarrando tracción sola y no tiene publicidad: acelerala con un ad.`,
        metricas: { patron: patron.id, serie: serieTexto, meses: meses.length, facturacionUltimoMes: factMes(serie.length - 1), stock: stockActual },
      })
    } else {
      declarados[patron.id] = (declarados[patron.id] || 0) + 1
    }
  }

  // Órdenes de prioridad dentro de cada sección:
  caida_matriz.sort((a, b) => (b._factPerdida || 0) - (a._factPerdida || 0))          // lo que más plata pierde, primero
  cronica_matriz.sort((a, b) => (b._capitalAtrapado || 0) - (a._capitalAtrapado || 0)) // capital atrapado DESC
  despertar_matriz.sort((a, b) => (b._factUltimoMes || 0) - (a._factUltimoMes || 0))   // el despegue más grande, primero

  return {
    secciones: { caida_matriz, cronica_matriz, despertar_matriz },
    excluidos: {
      ...declarados,
      sinVentasPeriodo,
      pausadas,
      caidaPorQuiebre,          // caídas con stock 0 → dominio de Velocidad (reponer)
      despertandoConAd,         // ya tiene ad inscripto
      despertandoSinStock,      // flywheel: sin stock no se publicita (Velocidad repone)
      despertandoAPerdida,
      despertandoSinEspacio,  // gate ads 15-jul: proyectado abajo de la mínima      // flywheel: a pérdida no se publicita (CN la trabaja)
      estrellaMargenNegativo,   // dominio de Contribución Neta (declarada)
      mesesVentana: meses.length,
      mesesLabels: meses.map(m => m.label),
      metrica: 'unidades',
    },
  }
}
